<template>
  <div class="family-order-review family-page bg-slate-50">
    <header
      class="family-page-header bg-white/90 backdrop-blur-md border-b border-slate-100"
      :style="{ paddingTop: 'env(safe-area-inset-top)' }"
    >
      <div class="px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          class="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-500"
          @click="goBack"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 class="text-base font-bold text-slate-900">评价护工</h1>
          <p class="text-xs text-slate-400">您的评价将帮助其他家属选择</p>
        </div>
      </div>
    </header>

    <main v-if="loading" class="family-page-body family-page-body--center text-sm text-slate-400">
      加载中...
    </main>

    <main v-else class="family-page-body px-4 py-4 pb-28">
      <section v-if="form" class="caregiver-card">
        <div class="caregiver-head">
          <div class="avatar-wrap">
            <img v-if="form.avatar" :src="resolveUrl(form.avatar)" class="avatar-img" alt="" />
            <div v-else class="avatar-fallback" :class="form.caregiver && form.caregiver.avatarBg">
              {{ caregiverInitial }}
            </div>
          </div>
          <div class="caregiver-meta">
            <div class="name-row">
              <h2 class="caregiver-name">{{ caregiverName }}</h2>
              <span v-if="form.level" class="level-tag">{{ form.level }}</span>
            </div>
            <div class="stats-row">
              <span class="stat-item">
                <svg class="w-3.5 h-3.5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                {{ form.rating || "5.0" }}
              </span>
              <span class="stat-dot" />
              <span class="stat-item">{{ form.orders || 0 }} 单服务</span>
              <span class="stat-dot" />
              <span class="stat-item">{{ form.experience || "1年" }}经验</span>
            </div>
            <p v-if="form.intro" class="caregiver-intro">{{ form.intro }}</p>
            <div v-if="form.tags && form.tags.length" class="tag-row">
              <span v-for="tag in form.tags" :key="tag" class="skill-tag">{{ tag }}</span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="historyReviews.length" class="history-card">
        <div class="section-head">
          <h3>历史评价</h3>
          <span class="section-sub">{{ historyReviews.length }} 条</span>
        </div>
        <div class="history-list">
          <article v-for="(item, idx) in historyReviews" :key="idx" class="history-item">
            <div class="history-top">
              <span class="history-user">{{ item.reviewerLabel || "家属用户" }}</span>
              <span class="history-time">{{ item.createTime }}</span>
            </div>
            <div class="history-stars">
              <span class="mini-score">综合 {{ item.starRating || "-" }}</span>
              <span v-if="item.professionalismRating" class="mini-score">专业 {{ item.professionalismRating }}</span>
              <span v-if="item.attitudeRating" class="mini-score">态度 {{ item.attitudeRating }}</span>
            </div>
            <p class="history-content">{{ item.content || "用户未填写文字评价" }}</p>
          </article>
        </div>
      </section>

      <section class="rating-card">
        <div class="section-head">
          <h3>本次服务评分</h3>
          <span class="section-sub">请逐项选择</span>
        </div>

        <div v-for="dim in dimensions" :key="dim.key" class="rate-row">
          <div class="rate-row-head">
            <div>
              <p class="rate-label">{{ dim.label }}</p>
              <p class="rate-desc">{{ dim.desc }}</p>
            </div>
            <span class="rate-badge" :class="{ filled: ratings[dim.key] > 0 }">
              {{ ratingText(ratings[dim.key]) }}
            </span>
          </div>
          <div class="rate-track">
            <button
              v-for="n in 5"
              :key="dim.key + '-' + n"
              type="button"
              class="rate-seg"
              :class="{ on: ratings[dim.key] >= n }"
              @click="setRating(dim.key, n)"
            >
              ★
            </button>
          </div>
        </div>
      </section>

      <section class="comment-card">
        <label class="block">
          <span class="comment-label">评价内容（选填）</span>
          <textarea
            v-model.trim="content"
            rows="4"
            maxlength="500"
            placeholder="分享本次陪护体验，如沟通、耐心、专业操作等..."
            class="review-field"
          />
        </label>
      </section>
    </main>

    <div
      class="fixed left-0 right-0 z-30 px-4 pb-4 bg-gradient-to-t from-slate-50 via-slate-50/95 to-transparent pt-3"
      :style="{ bottom: 'env(safe-area-inset-bottom)' }"
    >
      <button
        type="button"
        class="w-full py-3.5 rounded-xl text-white text-sm font-semibold bg-family-blue shadow-[0_6px_20px_rgb(0,130,212,0.35)] disabled:opacity-70"
        :disabled="submitting || !canSubmit"
        @click="handleSubmit"
      >
        {{ submitting ? "提交中..." : "提交评价" }}
      </button>
    </div>
  </div>
</template>

<script>
import { familyOrderApi } from "./familyOrderApi";
import { familyGoBack } from "./familyNavigation";

const RATING_LABELS = ["非常差", "较差", "一般", "满意", "非常满意"];

export default {
  name: "FamilyOrderReview",
  data() {
    return {
      loading: true,
      form: null,
      ratings: {
        professionalism: 0,
        attitude: 0,
        overall: 0,
      },
      content: "",
      submitting: false,
      dimensions: [
        { key: "professionalism", label: "专业程度", desc: "护理操作、病情观察、应急处理" },
        { key: "attitude", label: "服务态度", desc: "耐心细致、沟通配合、守时守约" },
        { key: "overall", label: "整体满意度", desc: "综合本次陪护服务的感受" },
      ],
    };
  },
  computed: {
    caregiverName() {
      return (this.form && this.form.caregiver && this.form.caregiver.name) || "";
    },
    caregiverInitial() {
      return this.caregiverName ? this.caregiverName.charAt(0) : "护";
    },
    historyReviews() {
      return (this.form && this.form.historyReviews) || [];
    },
    canSubmit() {
      return this.ratings.professionalism > 0
        && this.ratings.attitude > 0
        && this.ratings.overall > 0;
    },
  },
  mounted() {
    document.title = "评价护工 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    this.loadForm();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
  },
  methods: {
    ratingText(value) {
      if (!value || value < 1) return "待评分";
      return RATING_LABELS[value - 1];
    },
    setRating(key, value) {
      this.$set(this.ratings, key, value);
    },
    resolveUrl(url) {
      if (!url) return "";
      if (url.startsWith("http") || url.startsWith("data:")) return url;
      return url;
    },
    async loadForm() {
      this.loading = true;
      try {
        this.form = await familyOrderApi.reviewForm(this.$route.params.id);
      } catch (e) {
        this.$message.error((e && e.message) || "加载失败");
        this.goBack();
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      familyGoBack(this.$router, this.$route, "orders");
    },
    async handleSubmit() {
      if (this.submitting || !this.canSubmit) {
        if (!this.canSubmit) {
          this.$message.warning("请完成三项评分后再提交");
        }
        return;
      }
      this.submitting = true;
      try {
        await familyOrderApi.submitReview(this.$route.params.id, {
          professionalismRating: this.ratings.professionalism,
          attitudeRating: this.ratings.attitude,
          starRating: this.ratings.overall,
          content: this.content,
        });
        this.$message.success("评价成功，感谢您的反馈");
        this.$router.replace(`/family/order/${this.$route.params.id}`);
      } catch (e) {
        this.$message.error((e && e.message) || "提交失败");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.family-order-review {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}
.caregiver-card,
.history-card,
.rating-card,
.comment-card {
  background: #fff;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 12px rgb(0 0 0 / 0.04);
}
.caregiver-head {
  display: flex;
  gap: 0.875rem;
}
.avatar-wrap {
  flex-shrink: 0;
  width: 4.25rem;
  height: 4.25rem;
}
.avatar-img,
.avatar-fallback {
  width: 4.25rem;
  height: 4.25rem;
  border-radius: 1rem;
  object-fit: cover;
}
.avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #38bdf8, #0284c7);
}
.caregiver-meta {
  flex: 1;
  min-width: 0;
}
.name-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.caregiver-name {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
}
.level-tag {
  font-size: 10px;
  font-weight: 600;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
}
.stats-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #64748b;
}
.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
}
.stat-dot {
  width: 3px;
  height: 3px;
  border-radius: 999px;
  background: #cbd5e1;
}
.caregiver-intro {
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  color: #475569;
  line-height: 1.5;
}
.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.5rem;
}
.skill-tag {
  font-size: 10px;
  color: #64748b;
  background: #f8fafc;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
}
.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.section-head h3 {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
}
.section-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}
.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-height: 200px;
  overflow-y: auto;
}
.history-item {
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 0.75rem;
}
.history-top {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
}
.history-user {
  color: #334155;
  font-weight: 600;
}
.history-time {
  color: #94a3b8;
  flex-shrink: 0;
}
.history-stars {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.375rem;
}
.mini-score {
  font-size: 10px;
  color: #b45309;
  background: #fffbeb;
  padding: 0.125rem 0.375rem;
  border-radius: 999px;
}
.history-content {
  margin: 0.375rem 0 0;
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.5;
}
.rate-row + .rate-row {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #e2e8f0;
}
.rate-row-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
}
.rate-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}
.rate-desc {
  margin: 0.125rem 0 0;
  font-size: 0.6875rem;
  color: #94a3b8;
}
.rate-badge {
  flex-shrink: 0;
  font-size: 0.6875rem;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
}
.rate-badge.filled {
  color: #b45309;
  background: #fffbeb;
}
.rate-track {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.375rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  padding: 0.5rem;
}
.rate-seg {
  height: 2.25rem;
  border: none;
  border-radius: 0.5rem;
  background: #fff;
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
}
.rate-seg.on {
  color: #f59e0b;
  background: #fffbeb;
  box-shadow: 0 2px 8px rgb(245 158 11 / 0.2);
  transform: translateY(-1px);
}
.comment-label {
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;
}
.review-field {
  margin-top: 0.5rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background-color: #f1f5f9;
  font-size: 0.875rem;
  color: #1e293b;
  border: 1px solid transparent;
  resize: none;
  line-height: 1.5;
}
.review-field:focus {
  outline: none;
  border-color: #0082d4;
  background-color: #fff;
}
</style>
